/**
 * PerformanceMonitor - Track and analyze application performance
 * Monitors page load times, memory usage, and other key metrics
 */

class PerformanceMonitor {
    constructor() {
        this.metrics = new Map();
        this.observers = new Map();
        this.startTime = performance.now();
    }

    /**
     * Start tracking a performance metric
     * @param {string} name - Metric name
     * @param {object} options - Tracking options
     */
    startTracking(name, options = {}) {
        const startTime = performance.now();
        const memoryBefore = performance.memory ? performance.memory.usedJSHeapSize : 0;

        this.metrics.set(name, {
            startTime,
            memoryBefore,
            options,
            status: "tracking",
        });

        return () => this.stopTracking(name);
    }

    /**
     * Stop tracking a performance metric
     * @param {string} name - Metric name
     * @returns {object} - Performance data
     */
    stopTracking(name) {
        const metric = this.metrics.get(name);
        if (!metric || metric.status !== "tracking") {
            return null;
        }

        const endTime = performance.now();
        const memoryAfter = performance.memory ? performance.memory.usedJSHeapSize : 0;

        const data = {
            name,
            duration: endTime - metric.startTime,
            memoryDelta: memoryAfter - metric.memoryBefore,
            memoryBefore: metric.memoryBefore,
            memoryAfter: memoryAfter,
            timestamp: new Date().toISOString(),
            ...metric.options,
        };

        metric.status = "completed";
        metric.data = data;

        // Log performance data
        this.logPerformance(data);

        // Notify observers
        this.notifyObservers(name, data);

        return data;
    }

    /**
     * Track page load performance
     * @param {string} pageName - Name of the page
     * @returns {Function} - Stop tracking function
     */
    trackPageLoad(pageName) {
        return this.startTracking(`page_load_${pageName}`, {
            type: "page_load",
            page: pageName,
        });
    }

    /**
     * Track API request performance
     * @param {string} endpoint - API endpoint
     * @returns {Function} - Stop tracking function
     */
    trackApiRequest(endpoint) {
        return this.startTracking(`api_request_${endpoint}`, {
            type: "api_request",
            endpoint,
        });
    }

    /**
     * Track component render performance
     * @param {string} componentName - Component name
     * @returns {Function} - Stop tracking function
     */
    trackComponentRender(componentName) {
        return this.startTracking(`component_render_${componentName}`, {
            type: "component_render",
            component: componentName,
        });
    }

    /**
     * Get performance statistics
     * @returns {object} - Performance stats
     */
    getStats() {
        const completedMetrics = Array.from(this.metrics.values())
            .filter((metric) => metric.status === "completed")
            .map((metric) => metric.data);

        const pageLoads = completedMetrics.filter((m) => m.type === "page_load");
        const apiRequests = completedMetrics.filter((m) => m.type === "api_request");
        const componentRenders = completedMetrics.filter((m) => m.type === "component_render");

        return {
            totalMetrics: completedMetrics.length,
            pageLoads: {
                count: pageLoads.length,
                averageTime: this.calculateAverage(pageLoads, "duration"),
                slowest: this.findSlowest(pageLoads, "duration"),
                fastest: this.findFastest(pageLoads, "duration"),
            },
            apiRequests: {
                count: apiRequests.length,
                averageTime: this.calculateAverage(apiRequests, "duration"),
                slowest: this.findSlowest(apiRequests, "duration"),
                fastest: this.findFastest(apiRequests, "duration"),
            },
            componentRenders: {
                count: componentRenders.length,
                averageTime: this.calculateAverage(componentRenders, "duration"),
                slowest: this.findSlowest(componentRenders, "duration"),
                fastest: this.findFastest(componentRenders, "duration"),
            },
            memory: {
                current: performance.memory ? performance.memory.usedJSHeapSize : 0,
                peak: performance.memory ? performance.memory.peakJSHeapSize : 0,
                limit: performance.memory ? performance.memory.jsHeapSizeLimit : 0,
            },
            uptime: performance.now() - this.startTime,
        };
    }

    /**
     * Calculate average value for a property
     * @param {Array} metrics - Array of metric objects
     * @param {string} property - Property to average
     * @returns {number} - Average value
     */
    calculateAverage(metrics, property) {
        if (metrics.length === 0) return 0;
        const sum = metrics.reduce((acc, metric) => acc + metric[property], 0);
        return sum / metrics.length;
    }

    /**
     * Find slowest metric
     * @param {Array} metrics - Array of metric objects
     * @param {string} property - Property to compare
     * @returns {object|null} - Slowest metric or null
     */
    findSlowest(metrics, property) {
        if (metrics.length === 0) return null;
        return metrics.reduce((slowest, current) => (current[property] > slowest[property] ? current : slowest));
    }

    /**
     * Find fastest metric
     * @param {Array} metrics - Array of metric objects
     * @param {string} property - Property to compare
     * @returns {object|null} - Fastest metric or null
     */
    findFastest(metrics, property) {
        if (metrics.length === 0) return null;
        return metrics.reduce((fastest, current) => (current[property] < fastest[property] ? current : fastest));
    }

    /**
     * Log performance data
     * @param {object} data - Performance data
     */
    logPerformance(data) {
        const { name, duration, type, page, endpoint, component } = data;

        let message = `Performance: ${name} took ${duration.toFixed(2)}ms`;

        if (type === "page_load") {
            message += ` (Page: ${page})`;
        } else if (type === "api_request") {
            message += ` (API: ${endpoint})`;
        } else if (type === "component_render") {
            message += ` (Component: ${component})`;
        }

        // Log to console in development
        if (process.env.NODE_ENV === "development") {
            console.log(message);
        }

        // Send to analytics in production
        if (process.env.NODE_ENV === "production") {
            this.sendToAnalytics(data);
        }
    }

    /**
     * Send performance data to analytics
     * @param {object} data - Performance data
     */
    sendToAnalytics(data) {
        // Implement your analytics service here
        // Example: Google Analytics, Mixpanel, etc.
        if (window.gtag) {
            window.gtag("event", "performance", {
                event_category: "performance",
                event_label: data.name,
                value: Math.round(data.duration),
                custom_parameters: data,
            });
        }
    }

    /**
     * Add observer for performance events
     * @param {string} eventType - Type of event to observe
     * @param {Function} callback - Callback function
     */
    addObserver(eventType, callback) {
        if (!this.observers.has(eventType)) {
            this.observers.set(eventType, []);
        }
        this.observers.get(eventType).push(callback);
    }

    /**
     * Remove observer
     * @param {string} eventType - Type of event
     * @param {Function} callback - Callback function to remove
     */
    removeObserver(eventType, callback) {
        const observers = this.observers.get(eventType);
        if (observers) {
            const index = observers.indexOf(callback);
            if (index > -1) {
                observers.splice(index, 1);
            }
        }
    }

    /**
     * Notify observers of performance events
     * @param {string} eventType - Type of event
     * @param {object} data - Event data
     */
    notifyObservers(eventType, data) {
        const observers = this.observers.get(eventType);
        if (observers) {
            observers.forEach((callback) => {
                try {
                    callback(data);
                } catch (error) {
                    console.error("Performance observer error:", error);
                }
            });
        }
    }

    /**
     * Clear all metrics
     */
    clear() {
        this.metrics.clear();
    }

    /**
     * Export performance data
     * @returns {object} - Exported data
     */
    export() {
        return {
            metrics: Array.from(this.metrics.values())
                .filter((metric) => metric.status === "completed")
                .map((metric) => metric.data),
            stats: this.getStats(),
            timestamp: new Date().toISOString(),
        };
    }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Convenience functions
export const trackPageLoad = (pageName) => performanceMonitor.trackPageLoad(pageName);
export const trackApiRequest = (endpoint) => performanceMonitor.trackApiRequest(endpoint);
export const trackComponentRender = (componentName) => performanceMonitor.trackComponentRender(componentName);

export default performanceMonitor;
